import React, { useState } from "react";
import {
  Button,
  Box,
  Typography,
  TextField,
  MenuItem,
  Stack,
  Paper,
} from "@mui/material";
import axios from "axios";
import SlidingSidebar, {
  ButtonControls,
} from "../../../Components/SlidingSidebar";

interface AddProductProps {
  product?: any;
  categories: { value: string; label: string }[];
  formats: { id: number; item: string }[];
  unitsOfSale: { id: number; item: string }[];
  unitsOfMeasure: { id: number; label: string; value: string }[];
  brandId: number;
  onClose: () => void;
  onSave: (product: any) => void;
}

const AddProduct: React.FC<AddProductProps> = ({
  categories,
  formats,
  unitsOfSale,
  unitsOfMeasure,
  brandId,
  onClose,
  onSave,
}) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    product: "",
    category_id: "",
    msrp: "",
    case_pack: "",
    abv: "",
    format: "",
    size: { value: "", unit_of_measure: "ML" },
    unit_of_sale: "",
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormData({
      product: "",
      category_id: "",
      msrp: "",
      case_pack: "",
      abv: "",
      format: "",
      size: { value: "", unit_of_measure: "ML" },
      unit_of_sale: "",
    });
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const processedData = {
        ...formData,
        msrp: Number(formData.msrp),
        case_pack: Number(formData.case_pack),
        metadata: { abv: Number(formData.abv) },
        category_id: Number(formData.category_id),
        brand_id: brandId,
        size: JSON.stringify(formData.size),
        unit_of_sale: formData.unit_of_sale,
      };

      await axios.post(
        `${import.meta.env.VITE_API_URL}/brands/products`,
        processedData
      );
      handleClose();
      onSave(processedData);
    } catch (error) {
      console.error("Error saving product:", error);
    }
  };

  return (
    <>
      <Button variant="contained" onClick={handleOpen}>
        Add Product
      </Button>

      <SlidingSidebar open={open} onClose={handleClose} width="500px">
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
            <Stack spacing={0.5} sx={{ p: 2 }}>
              <Paper elevation={0} sx={{ p: 1 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Basic Information</Typography>
                  <TextField
                    label="Product Name"
                    name="product"
                    value={formData.product}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    select
                    label="Category"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ p: 1 }}>
                <Stack spacing={2}>
                  <Typography variant="h6">Product Details</Typography>
                  <TextField
                    label="MSRP"
                    name="msrp"
                    type="number"
                    value={formData.msrp}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: <span style={{ marginRight: 8 }}>$</span>,
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <TextField
                      sx={{ flex: 1 }}
                      label="Size Value"
                      name="size.value"
                      type="number"
                      value={formData.size.value}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          size: { ...prev.size, value: e.target.value },
                        }))
                      }
                      size="small"
                      required
                    />
                    <TextField
                      select
                      sx={{ width: "120px" }}
                      label="Unit"
                      name="size.unit_of_measure"
                      value={formData.size.unit_of_measure}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          size: {
                            ...prev.size,
                            unit_of_measure: e.target.value,
                          },
                        }))
                      }
                      size="small"
                      required
                    >
                      {unitsOfMeasure.map((uom) => (
                        <MenuItem key={uom.id} value={uom.value}>
                          {uom.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <TextField
                    label="Pack"
                    name="case_pack"
                    type="number"
                    value={formData.case_pack}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    label="ABV"
                    name="abv"
                    type="number"
                    value={formData.abv}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    select
                    label="Format"
                    name="format"
                    value={formData.format}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                  >
                    {formats.map((format) => (
                      <MenuItem key={format.id} value={format.item}>
                        {format.item}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Unit of Sale"
                    name="unit_of_sale"
                    value={formData.unit_of_sale}
                    onChange={handleInputChange}
                    size="small"
                    fullWidth
                    required
                  >
                    {unitsOfSale.map((uos) => (
                      <MenuItem key={uos.id} value={uos.item}>
                        {uos.item}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </Paper>
            </Stack>
          </Box>
          <ButtonControls>
            <Button type="submit" variant="contained" color="primary">
              Create Product
            </Button>
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>
          </ButtonControls>
        </Box>
      </SlidingSidebar>
    </>
  );
};

export default AddProduct;
